import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const spermFreeze: ContentBlock<"procedure"> = {
  id: makeId("proc-sperm-freeze"),
  kind: "procedure",
  tags: ["clinic-visit", "his", "sperm", "storage", "practical"],
  title: "Sperm Cryopreservation",
  subtitle: "Sperm freezing appointment for fertility preservation",
  body: {
    procedureName: "Sperm Cryopreservation (Sperm Freeze)",
    category: "lab",
    description:
      "A sperm cryopreservation appointment involves collecting a semen sample that is then processed and frozen for long-term storage. Frozen sperm can be used for IUI, IVF, or ICSI in the future.",
    howItWorks:
      "The process is similar to a semen analysis: you produce a sample by masturbation in a private room at the clinic or andrology lab. The sample is evaluated for count and motility, then processed using a cryoprotectant solution that shields sperm cells from ice-crystal damage during freezing. The prepared sample is divided into multiple vials or straws and slowly cooled before being transferred to liquid nitrogen tanks at -196 degrees Celsius. Frozen sperm can remain viable indefinitely. Most clinics recommend freezing two or more samples on separate days to maximize the number of vials available for future use. Annual storage fees apply.",
    duration: "30-45 minutes (including collection and paperwork)",
    costRange: { low: 300, high: 1000 },
    currency: "USD",
    prepInstructions: [
      "Abstain from ejaculation for 2-5 days before your appointment",
      "Avoid alcohol, smoking, and hot tubs for at least 72 hours before",
      "Complete any required infectious disease screening bloodwork before the freeze date",
      "Review and sign consent forms ahead of time if your clinic offers them electronically",
    ],
    whatToExpect: [
      "Paperwork including storage consent and disposition directives",
      "A private collection room for sample production",
      "The lab will analyze the sample before freezing and share a post-thaw survival estimate",
      "You may be advised to freeze additional samples on separate days for a better backup supply",
      "You will receive confirmation once the sample is frozen and stored",
      "Bring photo ID and insurance card or payment method",
      "Bring signed consent forms if provided in advance",
      "Bring infectious disease lab results if required by the facility",
    ],
    results: [
      {
        label: "Sample analysis",
        detail:
          "The lab usually checks count and motility before freezing and may estimate how well sperm are expected to survive thaw.",
      },
      {
        label: "Storage confirmation",
        detail:
          "You should receive confirmation that the sample was frozen, how many vials or straws were stored, and the storage terms.",
      },
      {
        label: "If low yield",
        detail:
          "Your clinic may recommend freezing another sample on a different day to build a stronger backup supply.",
      },
    ],
    forWhom: "him",
    frBody: {
      procedureName: "Cryoconservation de sperme",
      description:
        "Une consultation de cryoconservation de sperme consiste à recueillir un échantillon de sperme qui est ensuite préparé et congelé pour conservation à long terme. Le sperme congelé peut être utilisé pour une IIU (insémination intra-utérine), une FIV (fécondation in vitro) ou une ICSI (injection intracytoplasmique de spermatozoïde) ultérieures.",
      howItWorks:
        "Le déroulement est proche d'un spermogramme : vous produisez l'échantillon par masturbation dans une pièce privative à la clinique ou au laboratoire d'andrologie. L'échantillon est évalué (numération, mobilité) puis traité avec une solution cryoprotectrice protégeant les spermatozoïdes des cristaux de glace pendant la congélation. L'échantillon préparé est réparti dans plusieurs paillettes, refroidi progressivement, puis transféré dans des cuves d'azote liquide à -196 °C. Le sperme congelé reste viable de manière prolongée. La plupart des centres recommandent de congeler au moins deux échantillons à des jours différents pour disposer d'un plus grand nombre de paillettes. Des frais annuels de stockage s'appliquent.",
      duration:
        "30 à 45 minutes (recueil et formalités administratives compris)",
      prepInstructions: [
        "Observez une abstinence éjaculatoire de 2 à 5 jours avant le rendez-vous",
        "Évitez alcool, tabac et bains chauds au moins 72 heures avant",
        "Effectuez les bilans de dépistage des maladies infectieuses requis avant la congélation",
        "Lisez et signez les consentements à l'avance si votre centre les propose en ligne",
      ],
      whatToExpect: [
        "Formalités administratives, dont consentement de conservation et directives de devenir",
        "Pièce privative pour le recueil",
        "Le laboratoire analyse l'échantillon avant congélation et communique une estimation de survie après décongélation",
        "On peut vous conseiller de congeler d'autres échantillons à des jours différents pour disposer d'une meilleure réserve",
        "Vous recevrez une confirmation une fois l'échantillon congelé et stocké",
        "Apportez une pièce d'identité et votre carte Vitale/mutuelle ou un moyen de paiement",
        "Apportez les consentements signés s'ils ont été envoyés à l'avance",
        "Apportez les résultats du bilan infectieux si exigés par le centre",
      ],
      results: [
        {
          label: "Analyse de l'échantillon",
          detail:
            "Le laboratoire vérifie généralement numération et mobilité avant congélation et peut estimer la survie attendue après décongélation.",
        },
        {
          label: "Confirmation de stockage",
          detail:
            "Vous devriez recevoir une confirmation de congélation, le nombre de paillettes ou flacons stockés et les conditions de conservation.",
        },
        {
          label: "Si le rendement est faible",
          detail:
            "Votre centre peut recommander de congeler un autre échantillon un autre jour pour constituer une meilleure réserve.",
        },
      ],
    },
  },
  sources: [
    {
      title:
        "Fertility preservation in patients with medical indications: a committee opinion",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2026,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/fertility-preservation-in-patients-with-medical-indications-a-committee-opinion-2026/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "WHO Laboratory Manual for the Examination and Processing of Human Semen, 6th Edition",
      authors: "World Health Organization",
      journal: "WHO Publications",
      year: 2021,
      url: "https://www.who.int/publications/i/item/9789240030787",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(spermFreeze);
